import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, BookOpen, Users, CheckSquare, FileText, ArrowRight, Loader } from 'lucide-react'
import { supabase } from '../../shared/api/supabase'
import { useAuth } from '../../shared/contexts/AuthContext'

interface SearchResult {
  id: string
  type: 'curriculum' | 'group' | 'task' | 'lesson'
  title: string
  description?: string
  url: string
  metadata?: Record<string, any>
}

interface GlobalSearchProps {
  isOpen: boolean
  onClose: () => void
  searchQuery: string
}

export default function GlobalSearch({ isOpen, onClose, searchQuery }: GlobalSearchProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeCategory, setActiveCategory] = useState<'all' | 'curriculum' | 'groups' | 'tasks'>('all')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !searchQuery.trim() || !user) {
      setResults([])
      return
    }

    const performSearch = async () => {
      setLoading(true)
      try {
        const query = searchQuery.toLowerCase().trim()
        const allResults: SearchResult[] = []

        // Search curriculum
        if (activeCategory === 'all' || activeCategory === 'curriculum') {
          const { data: curricula } = await supabase
            .from('curriculum')
            .select('id, title, description')
            .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
            .limit(5)

          if (curricula) {
            curricula.forEach((curriculum) => {
              allResults.push({
                id: curriculum.id,
                type: 'curriculum',
                title: curriculum.title,
                description: curriculum.description || undefined,
                url: `/curriculum/${curriculum.id}/preview`,
                metadata: { type: 'curriculum' }
              })
            })
          }
        }

        // Search groups
        if (activeCategory === 'all' || activeCategory === 'groups') {
          const { data: groupMembers } = await supabase
            .from('group_members')
            .select('group_id, groups(id, name, description)')
            .eq('user_id', user.id)

          if (groupMembers) {
            groupMembers.forEach((member: any) => {
              const group = Array.isArray(member.groups) ? member.groups[0] : member.groups
              if (group && (group.name?.toLowerCase().includes(query) || group.description?.toLowerCase().includes(query))) {
                allResults.push({
                  id: group.id,
                  type: 'group',
                  title: group.name,
                  description: group.description || undefined,
                  url: `/groups/${group.id}`,
                  metadata: { type: 'group' }
                })
              }
            })
          }
        }

        // Search tasks
        if (activeCategory === 'all' || activeCategory === 'tasks') {
          const { data: tasks } = await supabase
            .from('task_assignments')
            .select('task_id, tasks(id, title, description), status')
            .eq('user_id', user.id)
            .limit(10)

          if (tasks) {
            tasks.forEach((assignment: any) => {
              const task = Array.isArray(assignment.tasks) ? assignment.tasks[0] : assignment.tasks
              if (task && (task.title?.toLowerCase().includes(query) || task.description?.toLowerCase().includes(query))) {
                allResults.push({
                  id: task.id,
                  type: 'task',
                  title: task.title,
                  description: task.description || undefined,
                  url: `/tasks`,
                  metadata: { type: 'task', status: assignment.status }
                })
              }
            })
          }
        }

        setResults(allResults)
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }

    // Debounce search
    const timeoutId = setTimeout(performSearch, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery, activeCategory, user, isOpen])

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url)
    onClose()
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'curriculum':
        return <BookOpen className="w-5 h-5" />
      case 'group':
        return <Users className="w-5 h-5" />
      case 'task':
        return <CheckSquare className="w-5 h-5" />
      case 'lesson':
        return <FileText className="w-5 h-5" />
      default:
        return <Search className="w-5 h-5" />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'curriculum':
        return 'Curriculum'
      case 'group':
        return 'Group'
      case 'task':
        return 'Task'
      case 'lesson':
        return 'Lesson'
      default:
        return 'Result'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999999] flex items-start justify-center pt-20 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[70vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Search</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Category Filters */}
          <div className="flex items-center gap-2">
            {(['all', 'curriculum', 'groups', 'tasks'] as const).map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-salt-navy text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-salt-gold" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {searchQuery.trim() ? 'No results found' : 'Start typing to search...'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-left flex items-start gap-4 group"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-salt-navy/10 dark:bg-salt-gold/20 rounded-lg flex items-center justify-center text-salt-navy dark:text-salt-gold">
                    {getIcon(result.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {result.title}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded">
                        {getTypeLabel(result.type)}
                      </span>
                    </div>
                    {result.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {result.description}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-salt-gold transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

